import React, {useEffect, useState} from "react";
import {Agent} from "../../models/Agent";
import {Box, Card, CardActionArea, CardContent, CircularProgress, Grid, Typography} from "@mui/material";
import {createAgent, getUserAgents} from "../../services/DataService";
import {useSelector} from "react-redux";
import {RootState} from "../../store/store";
import {setActiveAgent, setAgents} from "../../store/slices/agents.slice";
import {useNavigate} from "react-router-dom";
import {useAuth} from "../../context/useAuth";
import axios from "axios";
import {useAppDispatch} from "../../store/hooks";
import {setSelectedPageIndex} from "../../store/slices/nav.slice";
import {setSnackbarOpen} from "../../store/slices/snackbar.slice";
import {LightHoverButton} from "../../components/button/ButtonVariants";

interface AgentCardProps {
    agent: Agent;
    index: number;
}

export function AgentsPage() {
    const {token, logout} = useAuth();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const {selectedAgent, agents} = useSelector((state: RootState) => state.agents);
    const [selectedCardIndex, setSelectedCardIndex] = useState<number>();
    const [isCreating, setIsCreating] = useState(false);

    async function getAgents(token: string) {
        await getUserAgents(token)
            .then((response) => {
                let agents: Agent[] = response.data;
                agents.sort((a, b) => {
                    const nameA = a.gcpName.toUpperCase();
                    const nameB = b.gcpName.toUpperCase();
                    if (nameA < nameB) {
                        return -1;
                    }
                    if (nameA > nameB) {
                        return 1;
                    }
                    return 0;
                });
                dispatch(setAgents(agents));
            })
            .catch(function (error) {
                if (axios.isAxiosError(error) && error.response?.status === 401) {
                    logout();
                } else {
                    console.log(error);
                }
            })
    }

    const handleCreateAgent = async () => {
        if (!token) return;
        setIsCreating(true);
        try {
            const response = await createAgent(token);
            const newAgent: Agent = response.data;

            // Liste neu laden
            await getAgents(token);

            dispatch(setActiveAgent(newAgent));
            dispatch(setSelectedPageIndex(0));
            navigate(`/settings`);
        } catch (error) {
            if (axios.isAxiosError(error) && error.response?.status === 401) {
                logout();
            } else if (axios.isAxiosError(error)){
                dispatch(setSnackbarOpen({snackbarMessage: `Fehler beim Erstellen eines Agenten: ${error.response?.data.message}`, snackbarSeverity: 'error'}));
            } else {
                dispatch(setSnackbarOpen({snackbarMessage: `Fehler beim Erstellen eines Agenten: ${error}`, snackbarSeverity: 'error'}));
            }
        } finally {
            setIsCreating(false);
        }
    };

    useEffect(() => {
        if (token) {
            getAgents(token);
        }
    }, [token]);

    useEffect(() => {
        setSelectedCardIndex(agents.findIndex(agent => agent.id === selectedAgent?.id));
    }, [agents, selectedAgent]);

    const handleSelectAgent = (agent: Agent) => {
        dispatch(setActiveAgent(agent));
        dispatch(setSelectedPageIndex(0));
        navigate(`/settings`);
    }

    const AgentCard = (props: AgentCardProps) => {
        return (
            <Card variant="outlined" sx={{minHeight: 150, minWidth: 150}}>
                <CardActionArea
                    onClick={() => handleSelectAgent(props.agent)}
                    data-active={selectedCardIndex === props.index ? '' : undefined}
                    sx={{
                        height: '100%',
                        '&[data-active]': {
                            backgroundColor: 'action.selected',
                            '&:hover': {
                                backgroundColor: 'action.selectedHover',
                            },
                        },
                    }}
                >
                    <CardContent sx={{height: '100%'}}>
                        <Typography sx={{fontSize: 14}}>Agentenname</Typography>
                        <Typography variant='h5' component='div'>{props.agent.gcpName}</Typography>
                    </CardContent>
                </CardActionArea>
            </Card>
        )
    };

    return (
        <div style={{padding: "2rem"}}>
            <Grid container spacing={2}>
                {agents && agents.map((agent, index) => (
                    <AgentCard agent={agent} index={index} key={index}/>
                ))}
            </Grid>
            <Box mt={2}>
                <LightHoverButton
                    onClick={handleCreateAgent}
                    disabled={isCreating}
                    startIcon={isCreating ? <CircularProgress size={20}/> : null}
                >
                    {isCreating ? "Agent wird erstellt..." : "Neuen Agenten erstellen"}
                </LightHoverButton>
            </Box>
        </div>
    )
}